import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Flex,
  Icon,
  Badge,
  NativeSelect,
  IconButton,
  Spinner,
  NumberInput,
  useDisclosure,
  Tabs,
  Separator,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { TabList, Tab, TabPanels, TabPanel, Switch, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { toast } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { FaSave, FaMagic, FaEraser, FaExclamationTriangle, FaUsers, FaDrum, FaTimes } from 'react-icons/fa';
import {
  GiClarinet, GiFlute, GiSaxophone, GiTrombone, GiTrumpet, GiTuba,
  GiViolin, GiGuitar, GiFrenchHorn, GiAccordion, GiPianoKeys,
  GiDrum, GiMusicalNotes,
} from 'react-icons/gi';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { formacionesAPI, instrumentosAPI, musicosAPI } from '../services/api';
import { formatMusicoInstrumentosText, primeraFamiliaMusico } from '../utils/musicoInstrumentos';
import { IconoBomboMarcha } from './icons/IconoBomboMarcha';
import { IconoPlatillos } from './icons/IconoPlatillos';

// ── Colores por familia ──────────────────────────────────────────────────────
const FAMILIA_COLORS = {
  VIENTO_METAL:  { bg: '#FFF8E1', border: '#FFD54F', text: '#F57F17', dark: { bg: '#3D2E00', border: '#C49B00' } },
  VIENTO_MADERA: { bg: '#E8F5E9', border: '#81C784', text: '#2E7D32', dark: { bg: '#0D2E11', border: '#4CAF50' } },
  PERCUSION:     { bg: '#FFEBEE', border: '#E57373', text: '#C62828', dark: { bg: '#2E0000', border: '#EF5350' } },
  CUERDA:        { bg: '#E3F2FD', border: '#64B5F6', text: '#1565C0', dark: { bg: '#00142E', border: '#42A5F5' } },
  VOZ:           { bg: '#F3E5F5', border: '#BA68C8', text: '#6A1B9A', dark: { bg: '#200030', border: '#AB47BC' } },
  OTRO:          { bg: '#F5F5F5', border: '#BDBDBD', text: '#424242', dark: { bg: '#1A1A1A', border: '#757575' } },
};

const FAMILIA_LABELS = {
  VIENTO_METAL: 'Viento Metal',
  VIENTO_MADERA: 'Viento Madera',
  PERCUSION: 'Percusión',
  CUERDA: 'Cuerda',
  VOZ: 'Voz',
  OTRO: 'Otro',
};

// Emoji representativo de cada familia (fallback)
const FAMILIA_EMOJI = {
  VIENTO_METAL:  '🎺',
  VIENTO_MADERA: '🎷',
  PERCUSION:     '🥁',
  CUERDA:        '🎸',
  VOZ:           '🎤',
  OTRO:          '🎵',
};

// Mapa por nombre de instrumento específico (minúsculas, sin tildes)
const INSTRUMENTO_EMOJI_MAP = {
  // Viento Metal – trompeta/corneta family → 🎺
  'trompeta': '🎺', 'trompeta piccolo': '🎺', 'corneta': '🎺',
  'cornetin': '🎺', 'bugle': '🎺', 'fliscorno': '🎺', 'flugel': '🎺',
  // Viento Metal – trompa/trombón/tuba family → 📯
  'trompa': '📯', 'corno': '📯', 'corno frances': '📯',
  'trombon': '📯', 'trombon tenor': '📯', 'trombon bajo': '📯',
  'trombon contrabajo': '📯', 'bombardino': '📯', 'euphonium': '📯',
  'tuba': '📯', 'tuba baja': '📯', 'helicon': '📯', 'sousafon': '📯',
  // Viento Metal – saxofón → 🎷 (aunque es madera, suele agruparse)
  'saxofon soprano': '🎷', 'saxofon alto': '🎷',
  'saxofon tenor': '🎷', 'saxofon baritono': '🎷', 'saxofon contrabajo': '🎷',
  // Viento Madera – flauta → 🪈
  'flauta': '🪈', 'flauta travesera': '🪈', 'flauta traversa': '🪈',
  'flauta traversera': '🪈', 'flauta de pico': '🪈',
  'flautin': '🪈', 'piccolo': '🪈',
  // Viento Madera – clarinete/oboe/fagot → 🎶
  'clarinete': '🎶', 'clarinete soprano': '🎶', 'clarinete en sib': '🎶',
  'clarinete en la': '🎶', 'clarinete alto': '🎶', 'clarinete bajo': '🎶',
  'clarinete contrabajo': '🎶', 'corno ingles': '🎶',
  'oboe': '🎶', 'oboe d amore': '🎶', 'fagot': '🎶', 'contrafagot': '🎶',
  // Percusión
  'caja': '🥁', 'redoblante': '🥁', 'tambor': '🥁',
  'bombo': '🥁', 'bombo con platos': '🥁', 'bombo y platos': '🥁',
  'timbal': '🪘', 'timbal de calle': '🪘', 'timbal orquestal': '🪘',
  'lira': '🔔', 'glockenspiel': '🔔', 'campana': '🔔', 'campanofon': '🔔',
  'vibrafono': '🎹', 'xilofono': '🎹', 'marimba': '🎹',
  'platillos': '🔔', 'platos': '🔔', 'tam tam': '🥁',
  // Cuerda
  'violin': '🎻', 'viola': '🎻', 'violonchelo': '🎻',
  'cello': '🎻', 'contrabajo de cuerda': '🎻', 'arpa': '🎻',
  'guitarra': '🎸', 'bajo electrico': '🎸',
  // Teclado / otros
  'piano': '🎹', 'teclado': '🎹', 'organo': '🎹', 'acordeon': '🪗',
};

function normNombre(n) {
  return (n || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
}

/** Devuelve el emoji más específico posible para un instrumento */
function getInstrumentoEmoji(nombre, familia) {
  if (nombre) {
    const key = normNombre(nombre);
    if (INSTRUMENTO_EMOJI_MAP[key]) return INSTRUMENTO_EMOJI_MAP[key];
    // Búsqueda parcial: si el nombre empieza por una clave conocida
    for (const [k, emoji] of Object.entries(INSTRUMENTO_EMOJI_MAP)) {
      if (key.startsWith(k + ' ') || key === k) return emoji;
    }
  }
  return FAMILIA_EMOJI[familia] || '🎵';
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const FAMILIA_SORT_ORDER = { VIENTO_METAL: 0, VIENTO_MADERA: 1, PERCUSION: 2, CUERDA: 3, VOZ: 4, OTRO: 5 };

function sortMusicosList(list, orden) {
  const copy = [...list];
  if (orden === 'familia') {
    copy.sort((a, b) => {
      const fa = primeraFamiliaMusico(a) || 'OTRO';
      const fb = primeraFamiliaMusico(b) || 'OTRO';
      const famDiff = (FAMILIA_SORT_ORDER[fa] ?? 5) - (FAMILIA_SORT_ORDER[fb] ?? 5);
      if (famDiff !== 0) return famDiff;
      // Secundario: por nombre de instrumento principal
      const iaA = a.instrumentosAsignados?.[0];
      const iaB = b.instrumentosAsignados?.[0];
      const instrA = iaA?.instrumento?.nombre || iaA?.instrumentoPersonalizado?.nombre || '';
      const instrB = iaB?.instrumento?.nombre || iaB?.instrumentoPersonalizado?.nombre || '';
      const instrDiff = instrA.localeCompare(instrB, 'es', { sensitivity: 'base' });
      if (instrDiff !== 0) return instrDiff;
      // Terciario: por nombre del músico
      return `${a.nombre} ${a.apellidos || ''}`.localeCompare(`${b.nombre} ${b.apellidos || ''}`, 'es');
    });
  } else {
    copy.sort((a, b) =>
      `${a.nombre} ${a.apellidos || ''}`.localeCompare(`${b.nombre} ${b.apellidos || ''}`, 'es')
    );
  }
  return copy;
}

function getIaDisplay(ia) {
  return ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || '';
}

function getIaFamilia(ia) {
  return ia?.instrumento?.familia || ia?.instrumentoPersonalizado?.familia || 'OTRO';
}

function getIaVoz(ia) {
  return ia?.vozPartituraPersonalizada?.nombre || null;
}

function getFirstIaId(musico) {
  return musico?.instrumentosAsignados?.[0]?.id || null;
}

/** Quita posiciones persistidas con músico (el estado vivo está en `asignaciones`). */
function withoutMusicianPositions(posiciones, cells) {
  if (!cells.length) return posiciones;
  return posiciones.filter((p) => {
    if (!p.musicoId) return true;
    return !cells.some((c) => c.row === p.fila && c.col === p.columna);
  });
}

// Mapa de iconos SVG específicos por instrumento (react-icons/gi)
const INSTRUMENTO_ICON_MAP = {
  // Viento Metal
  'trompeta': GiTrumpet, 'corneta': GiTrumpet, 'cornetin': GiTrumpet,
  'fliscorno': GiTrumpet, 'bugle': GiTrumpet,
  'trompa': GiFrenchHorn, 'corno': GiFrenchHorn, 'corno frances': GiFrenchHorn,
  'trombon': GiTrombone, 'trombon tenor': GiTrombone, 'trombon bajo': GiTrombone,
  'trombon contrabajo': GiTrombone, 'euphonium': GiTrombone,
  'tuba': GiTuba, 'tuba baja': GiTuba, 'helicon': GiTuba, 'sousafon': GiTuba,
  'bombardino': GiTuba,
  'saxofon soprano': GiSaxophone, 'saxofon alto': GiSaxophone,
  'saxofon tenor': GiSaxophone, 'saxofon baritono': GiSaxophone, 'saxofon contrabajo': GiSaxophone,
  // Viento Madera
  'flauta': GiFlute, 'flauta travesera': GiFlute, 'flauta traversa': GiFlute,
  'flauta traversera': GiFlute, 'flauta de pico': GiFlute, 'flautin': GiFlute, 'piccolo': GiFlute,
  'clarinete': GiClarinet, 'clarinete soprano': GiClarinet, 'clarinete en sib': GiClarinet,
  'clarinete en la': GiClarinet, 'clarinete alto': GiClarinet,
  'clarinete bajo': GiClarinet, 'clarinete contrabajo': GiClarinet,
  'oboe': GiMusicalNotes, 'fagot': GiMusicalNotes, 'contrafagot': GiMusicalNotes,
  // Percusión
  'bombo': IconoBomboMarcha, 'bombo con platos': IconoBomboMarcha, 'bombo y platos': IconoBomboMarcha,
  'caja': GiDrum, 'redoblante': GiDrum, 'tambor': GiDrum,
  'platos': IconoPlatillos, 'platillos': IconoPlatillos,
  // Cuerda
  'violin': GiViolin, 'viola': GiViolin, 'violonchelo': GiViolin, 'cello': GiViolin,
  'guitarra': GiGuitar, 'bajo electrico': GiGuitar,
  // Teclado
  'piano': GiPianoKeys, 'teclado': GiPianoKeys, 'organo': GiPianoKeys,
  'acordeon': GiAccordion,
};

// Ajuste de tamaño relativo por instrumento (en unidades de boxSize)
const INSTRUMENTO_SIZE_OFFSET = {
  'bombardino': -1,
};

/** Componente que renderiza el icono SVG del instrumento (gi) o emoji como fallback */
function InstrIcon({ nombre, familia, boxSize = 5, color }) {
  const key = normNombre(nombre || '');
  let IconComp = INSTRUMENTO_ICON_MAP[key];
  if (!IconComp && key) {
    for (const [k, ic] of Object.entries(INSTRUMENTO_ICON_MAP)) {
      if (key.startsWith(k + ' ') || key === k) { IconComp = ic; break; }
    }
  }
  const sizeOffset = INSTRUMENTO_SIZE_OFFSET[key] ?? 0;
  const finalSize = Math.max(3, boxSize + sizeOffset);
  if (IconComp) return <Icon boxSize={finalSize} color={color} asChild><IconComp /></Icon>;
  return <>{getInstrumentoEmoji(nombre, familia)}</>;
}

// ── DraggableInstrument (para definir posiciones) ────────────────────────────
function DraggableInstrument({ instrument, type }) {
  const id = `inst-${type}-${instrument.id}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data: { instrument, type } });
  const colors = FAMILIA_COLORS[instrument.familia] || FAMILIA_COLORS.OTRO;

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      bg={colors.bg}
      border="1.5px solid"
      borderColor={colors.border}
      rounded="lg"
      px={2}
      py={1.5}
      fontSize="xs"
      fontWeight="600"
      color={colors.text}
      cursor="grab"
      opacity={isDragging ? 0.4 : 1}
      _hover={{ shadow: 'md', transform: 'scale(1.03)' }}
      transition="all 0.15s"
      userSelect="none"
    >
      <HStack gap={1.5}>
        <InstrIcon nombre={instrument.nombre} familia={instrument.familia} boxSize={4} color={colors.text} />
        <Text lineClamp={1}>{instrument.nombre}</Text>
      </HStack>
    </Box>
  );
}

// ── DraggableMusician ────────────────────────────────────────────────────────
function DraggableMusician({ musico }) {
  const id = `musico-${musico.id}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data: { musico } });
  const familia = primeraFamiliaMusico(musico) || 'OTRO';
  const colors = FAMILIA_COLORS[familia] || FAMILIA_COLORS.OTRO;
  const firstIa = musico?.instrumentosAsignados?.[0] || null;
  const firstNombre = firstIa?.instrumento?.nombre || firstIa?.instrumentoPersonalizado?.nombre || null;
  const instrText = formatMusicoInstrumentosText(musico, ' · ');

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      bg={colors.bg}
      border="1.5px solid"
      borderColor={colors.border}
      rounded="xl"
      px={3}
      py={2}
      cursor="grab"
      opacity={isDragging ? 0.4 : 1}
      _hover={{ shadow: 'md', transform: 'translateY(-1px)' }}
      transition="all 0.15s"
      userSelect="none"
    >
      <HStack gap={2} align="center">
        <InstrIcon nombre={firstNombre} familia={familia} boxSize={6} color={colors.text} />
        <VStack gap={0} align="start" flex={1} minW={0}>
          <Text fontWeight="700" fontSize="xs" color={colors.text} lineClamp={1}>
            {musico.nombre} {musico.apellidos || ''}
          </Text>
          <Text fontSize="2xs" color="gray.500" lineClamp={1}>{instrText}</Text>
        </VStack>
      </HStack>
    </Box>
  );
}

// ── DraggableGroup (GRID mode) ───────────────────────────────────────────────
function DraggableGroup({ grupo, availableCount }) {
  const id = `grupo-${grupo.id}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { grupo },
    disabled: availableCount === 0,
  });

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      bg={availableCount === 0 ? 'gray.100' : 'purple.50'}
      border="1.5px solid"
      borderColor={availableCount === 0 ? 'gray.300' : 'purple.300'}
      rounded="xl"
      px={3}
      py={2}
      cursor={availableCount === 0 ? 'not-allowed' : 'grab'}
      opacity={isDragging ? 0.4 : availableCount === 0 ? 0.5 : 1}
      _hover={availableCount > 0 ? { shadow: 'md', transform: 'translateY(-1px)' } : {}}
      transition="all 0.15s"
      userSelect="none"
    >
      <HStack gap={2} align="center">
        <Icon
          boxSize={4}
          color={availableCount === 0 ? 'gray.400' : 'purple.500'}
          asChild><FaUsers /></Icon>
        <VStack gap={0} align="start" flex={1} minW={0}>
          <Text fontWeight="700" fontSize="xs" color={availableCount === 0 ? 'gray.400' : 'purple.700'} lineClamp={1}>
            {grupo.nombre}
          </Text>
          <Text fontSize="2xs" color="gray.500" lineClamp={1}>
            {availableCount}/{grupo.miembros?.length || 0} disponibles
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

// Filas especiales (fuera del rango 0..N-1)
const DIRECTOR_ROW  = -1;  // fila del director (1 celda centrada)
const EXTRA_BEFORE  = -2;  // fila extraordinaria delante del director
const EXTRA_AFTER   = -3;  // fila extraordinaria detrás del director

// ── GridCell ─────────────────────────────────────────────────────────────────
function GridCell({ row, col, position, asignacion, onClear, isHighlight, onChangeIa, specialLabel }) {
  const id = `cell-${row}-${col}`;
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id, data: { row, col } });

  const musico = asignacion?.musico || null;
  const iaId = asignacion?.iaId || null;

  // Draggable desde celda (sólo si tiene músico)
  const {
    attributes: dragAttrs,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `cell-drag-${row}-${col}`,
    data: { fromCell: true, row, col, musico, iaId },
    disabled: !musico,
  });

  const setNodeRef = el => { setDropRef(el); setDragRef(el); };

  // Find the selected instrument assignment
  const ia = musico?.instrumentosAsignados?.find(x => x.id === iaId) || musico?.instrumentosAsignados?.[0] || null;
  const hasMultiInstr = (musico?.instrumentosAsignados?.length || 0) > 1;

  const displayFamilia = ia ? getIaFamilia(ia) : (primeraFamiliaMusico(musico) || 'OTRO');
  const displayInstrName = ia ? getIaDisplay(ia) : '';
  const displayVoz = ia ? getIaVoz(ia) : null;

  // Position-only (no musician assigned)
  const instName = position?.instrumento?.nombre || position?.instrumentoPersonalizado?.nombre || position?.label || '';
  const instFamilia = position?.instrumento?.familia || position?.instrumentoPersonalizado?.familia || null;
  const instColors = instFamilia ? (FAMILIA_COLORS[instFamilia] || FAMILIA_COLORS.OTRO) : null;

  // Cell styling
  let bg = 'gray.50';
  let borderColor = 'gray.200';
  let borderStyle = 'dashed';

  if (isOver) {
    bg = 'purple.50'; borderColor = 'purple.400'; borderStyle = 'solid';
  } else if (musico && !isDragging) {
    const mColors = FAMILIA_COLORS[displayFamilia] || FAMILIA_COLORS.OTRO;
    bg = mColors.bg; borderColor = mColors.border; borderStyle = 'solid';
  } else if (position) {
    bg = instColors?.bg || 'gray.100'; borderColor = instColors?.border || 'gray.300'; borderStyle = 'dashed';
  }
  if (isHighlight) { borderColor = 'red.400'; borderStyle = 'solid'; }

  return (
    <Box
      ref={setNodeRef}
      {...(musico ? dragListeners : {})}
      {...(musico ? dragAttrs : {})}
      bg={bg}
      border="2px"
      borderStyle={borderStyle}
      borderColor={borderColor}
      rounded="lg"
      minH="88px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      p={1}
      position="relative"
      transition="all 0.15s"
      overflow="hidden"
      cursor={musico && !isDragging ? 'grab' : 'default'}
    >
      {musico && !isDragging ? (
        <VStack gap={0} align="center" w="full">
          <InstrIcon nombre={displayInstrName} familia={displayFamilia} boxSize={6} color={(FAMILIA_COLORS[displayFamilia] || FAMILIA_COLORS.OTRO).text} />
          <Text fontSize="2xs" fontWeight="700" textAlign="center" lineClamp={1} w="full" px={1}>
            {musico.nombre}
          </Text>
          {!hasMultiInstr && displayInstrName && (
            <Text fontSize="2xs" color="gray.500" textAlign="center" lineClamp={1}>{displayInstrName}</Text>
          )}
          {!hasMultiInstr && displayVoz && (
            <Text fontSize="2xs" color="purple.600" textAlign="center" lineClamp={1} fontStyle="italic">
              {displayVoz}
            </Text>
          )}
          {hasMultiInstr && (
            <NativeSelect.Root>
              <NativeSelect.Field
                size="xs"
                variant="filled"
                value={iaId || ''}
                onChange={e => onChangeIa(row, col, e.target.value)}
                fontSize="2xs"
                mt={0.5}
                h="18px"
                borderRadius="md"
                onClick={e => e.stopPropagation()}>
                {musico.instrumentosAsignados.map(x => (
                  <option key={x.id} value={x.id}>
                    {getIaDisplay(x)}{getIaVoz(x) ? ` · ${getIaVoz(x)}` : ''}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          )}
          {hasMultiInstr && displayVoz && (
            <Text fontSize="2xs" color="purple.600" textAlign="center" lineClamp={1} fontStyle="italic">
              {displayVoz}
            </Text>
          )}
          <IconButton
            size="sm"
            variant="ghost"
            colorPalette="red"
            position="absolute"
            top={1}
            right={1}
            zIndex={1}
            minW="28px"
            h="28px"
            borderRadius="md"
            bg="whiteAlpha.900"
            _hover={{ bg: 'red.50' }}
            onClick={e => { e.stopPropagation(); onClear(row, col); }}
            aria-label="Quitar músico"><Icon boxSize={3.5} asChild><FaEraser /></Icon></IconButton>
        </VStack>
      ) : position ? (
        <VStack gap={0} align="center">
          <Box opacity={0.45}><InstrIcon nombre={instName} familia={instFamilia} boxSize={5} color={instColors?.text} /></Box>
          <Text fontSize="2xs" fontWeight="600" color={instColors?.text || 'gray.500'} textAlign="center" lineClamp={2} px={1}>
            {instName}
          </Text>
          <IconButton
            size="sm"
            variant="ghost"
            colorPalette="red"
            position="absolute"
            top={1}
            right={1}
            zIndex={1}
            minW="28px"
            h="28px"
            borderRadius="md"
            bg="whiteAlpha.900"
            _hover={{ bg: 'red.50' }}
            onClick={e => { e.stopPropagation(); onClear(row, col); }}
            aria-label="Quitar posición"><Icon boxSize={3.5} asChild><FaEraser /></Icon></IconButton>
        </VStack>
      ) : (
        specialLabel
          ? <Text fontSize="2xs" color="gray.400" textAlign="center" fontStyle="italic" px={1}>{specialLabel}</Text>
          : <Text fontSize="xs" color="gray.300" userSelect="none">·</Text>
      )}
    </Box>
  );
}

// ── Guías SVG del lienzo ─────────────────────────────────────────────────────
function CanvasGuiaTeatro() {
  return (
    <Box position="absolute" inset={0} w="100%" h="100%" opacity={0.13} pointerEvents="none" asChild><svg>
        {[85, 70, 55, 40].map(r => (
          <ellipse key={r}
            cx="50%" cy="95%"
            rx={`${r}%`} ry={`${r * 0.55}%`}
            fill="none" stroke="currentColor" strokeWidth="1.5"
            strokeDasharray="6 4"
          />
        ))}
        <line x1="5%" y1="90%" x2="95%" y2="90%" stroke="currentColor" strokeWidth="2" />
      </svg></Box>
  );
}

function CanvasGuiaCalle() {
  return (
    <Box position="absolute" inset={0} w="100%" h="100%" pointerEvents="none" asChild><svg>
        {/* Acera izquierda */}
        <rect x="2%" y="0" width="12%" height="100%" fill="currentColor" opacity={0.06} rx="4" />
        <line x1="14%" y1="0" x2="14%" y2="100%" stroke="currentColor" strokeWidth="2" opacity={0.18} strokeDasharray="8 5" />
        {/* Acera derecha */}
        <rect x="86%" y="0" width="12%" height="100%" fill="currentColor" opacity={0.06} rx="4" />
        <line x1="86%" y1="0" x2="86%" y2="100%" stroke="currentColor" strokeWidth="2" opacity={0.18} strokeDasharray="8 5" />
        {/* Línea central de la calzada (discontinua) */}
        <line x1="50%" y1="5%" x2="50%" y2="95%" stroke="currentColor" strokeWidth="1" opacity={0.1} strokeDasharray="12 8" />
        {/* Flecha de dirección de marcha (arriba) */}
        <g opacity={0.22}>
          <line x1="50%" y1="85%" x2="50%" y2="12%" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <polyline points="44%,22% 50%,10% 56%,22%" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        </g>
      </svg></Box>
  );
}

// ── LibreCanvasEditor ─────────────────────────────────────────────────────────
function LibreCanvasEditor({ plantilla, musicos, posiciones, toast, onSaved, grupos }) {
  // librePositions: { [musicoId]: { musico, x, y, iaId } }
  const [librePositions, setLibrePositions] = useState({});
  const [selectedMusico, setSelectedMusico] = useState(null); // músico seleccionado para colocar (click)
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [dragging, setDragging] = useState(null);             // reposicionar músico ya colocado
  // dragIntent: arrastrar desde paleta → { musico/grupo, startX, startY, isDragging, cursorX, cursorY }
  const [dragIntent, setDragIntent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sortOrder, setSortOrder] = useState('familia');
  const [subtipoLibre, setSubtipoLibre] = useState(plantilla.subtipoLibre || 'teatro');
  const [sideTab, setSideTab] = useState(0);
  const canvasRef = useRef(null);
  const canvasBg = useColorModeValue('gray.100', 'gray.700');
  const sideBg = useColorModeValue('white', 'gray.800');
  const selectorBg = useColorModeValue('white', 'gray.800');
  const selectorBorder = useColorModeValue('gray.200', 'gray.600');

  // Reconstruir desde posiciones guardadas
  useEffect(() => {
    const initial = {};
    posiciones.forEach(p => {
      if (p.musico && p.x != null && p.y != null) {
        const ia = p.musico.instrumentosAsignados?.find(
          ia => ia.instrumentoId === p.instrumentoId ||
                ia.instrumentoPersonalizadoId === p.instrumentoPersonalizadoId
        ) || p.musico.instrumentosAsignados?.[0];
        initial[p.musico.id] = { musico: p.musico, x: p.x, y: p.y, iaId: ia?.id || null };
      }
    });
    setLibrePositions(initial);
  }, [posiciones]);

  const placedIds = new Set(Object.keys(librePositions));
  const availableMusicos = sortMusicosList(musicos.filter(m => !placedIds.has(m.id)), sortOrder);

  const getGrupoAvailableMembers = useCallback((grupo) => {
    return (grupo.miembros || [])
      .map(gm => musicos.find(m => m.id === (gm.musico?.id || gm.musicoId)))
      .filter(m => m && !placedIds.has(m.id));
  }, [musicos, placedIds]);

  const placeGrupoOnCanvas = useCallback((grupo, startX, startY) => {
    const members = getGrupoAvailableMembers(grupo);
    if (members.length === 0) return;
    const SPACING_X = 8;
    const SPACING_Y = 10;
    const newPositions = {};
    let cx = startX;
    let cy = startY;
    for (const m of members) {
      if (cx > 92) { cx = startX; cy += SPACING_Y; }
      if (cy > 88) cy = 88;
      newPositions[m.id] = { musico: m, x: Math.max(2, Math.min(92, cx)), y: Math.max(2, Math.min(88, cy)), iaId: getFirstIaId(m) };
      cx += SPACING_X;
    }
    setLibrePositions(prev => ({ ...prev, ...newPositions }));
  }, [getGrupoAvailableMembers]);

  // Drag desde la paleta — listeners globales activos solo durante el gesto
  useEffect(() => {
    if (!dragIntent) return;
    const THRESHOLD = 5;

    const handleMouseMove = (e) => {
      setDragIntent(prev => {
        if (!prev) return null;
        const dx = Math.abs(e.clientX - prev.startX);
        const dy = Math.abs(e.clientY - prev.startY);
        return { ...prev, isDragging: prev.isDragging || dx > THRESHOLD || dy > THRESHOLD, cursorX: e.clientX, cursorY: e.clientY };
      });
    };

    const handleMouseUp = (e) => {
      setDragIntent(prev => {
        if (!prev) return null;
        if (prev.isDragging && canvasRef.current) {
          const rect = canvasRef.current.getBoundingClientRect();
          const inCanvas = e.clientX >= rect.left && e.clientX <= rect.right &&
                           e.clientY >= rect.top  && e.clientY <= rect.bottom;
          if (inCanvas) {
            const x = Math.max(2, Math.min(92, ((e.clientX - rect.left) / rect.width) * 100));
            const y = Math.max(2, Math.min(88, ((e.clientY - rect.top)  / rect.height) * 100));
            if (prev.grupo) {
              placeGrupoOnCanvas(prev.grupo, x, y);
              setSelectedGrupo(null);
            } else {
              const iaId = getFirstIaId(prev.musico);
              setLibrePositions(lp => ({ ...lp, [prev.musico.id]: { musico: prev.musico, x, y, iaId } }));
              setSelectedMusico(null);
            }
            return null;
          }
        }
        if (!prev.isDragging) {
          if (prev.grupo) {
            setSelectedGrupo(sel => sel?.id === prev.grupo.id ? null : prev.grupo);
            setSelectedMusico(null);
          } else {
            setSelectedMusico(sel => sel?.id === prev.musico.id ? null : prev.musico);
            setSelectedGrupo(null);
          }
        }
        return null;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragIntent, placeGrupoOnCanvas]);

  const getCanvasXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.max(2, Math.min(92, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(2, Math.min(88, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  };

  const handleCanvasClick = (e) => {
    if (dragging) return;
    if (selectedGrupo) {
      const { x, y } = getCanvasXY(e);
      placeGrupoOnCanvas(selectedGrupo, x, y);
      setSelectedGrupo(null);
      return;
    }
    if (!selectedMusico) return;
    const { x, y } = getCanvasXY(e);
    const iaId = getFirstIaId(selectedMusico);
    setLibrePositions(prev => ({ ...prev, [selectedMusico.id]: { musico: selectedMusico, x, y, iaId } }));
    setSelectedMusico(null);
  };

  const handlePlacedMouseDown = (musicoId, e) => {
    e.stopPropagation();
    e.preventDefault();
    const pos = librePositions[musicoId];
    setDragging({ musicoId, startCX: e.clientX, startCY: e.clientY, origX: pos.x, origY: pos.y });
  };

  const handleCanvasMouseMove = (e) => {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragging.startCX) / rect.width) * 100;
    const dy = ((e.clientY - dragging.startCY) / rect.height) * 100;
    setLibrePositions(prev => ({
      ...prev,
      [dragging.musicoId]: {
        ...prev[dragging.musicoId],
        x: Math.max(2, Math.min(92, dragging.origX + dx)),
        y: Math.max(2, Math.min(88, dragging.origY + dy)),
      },
    }));
  };

  const handleCanvasMouseUp = () => setDragging(null);

  const handleRemove = (musicoId) =>
    setLibrePositions(prev => { const u = { ...prev }; delete u[musicoId]; return u; });

  const handleChangeIaLibre = (musicoId, iaId) =>
    setLibrePositions(prev => ({ ...prev, [musicoId]: { ...prev[musicoId], iaId } }));

  const handleSave = async () => {
    try {
      setSaving(true);
      // Persistir el subtipo de lienzo si cambió
      if (subtipoLibre !== (plantilla.subtipoLibre || 'teatro')) {
        await formacionesAPI.updatePlantilla(plantilla.id, { subtipoLibre });
      }
      const posData = Object.values(librePositions).map((pos, idx) => {
        const ia = pos.musico.instrumentosAsignados?.find(x => x.id === pos.iaId)
          || pos.musico.instrumentosAsignados?.[0];
        return {
          fila: idx,
          columna: 0,
          x: pos.x,
          y: pos.y,
          musicoId: pos.musico.id,
          instrumentoId: ia?.instrumentoId || null,
          instrumentoPersonalizadoId: ia?.instrumentoPersonalizadoId || null,
          label: pos.musico.nombre,
        };
      });
      await formacionesAPI.savePosiciones(plantilla.id, posData);
      toast({ title: 'Formación guardada', status: 'success' });
      onSaved?.();
    } catch {
      toast({ title: 'Error al guardar', status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box px={{ base: 2, md: 4 }} py={{ base: 3, md: 4 }}>
      {/* Cabecera */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={2} mb={3}>
        <HStack>
          <Heading size="md">{plantilla.nombre}</Heading>
          <Badge colorPalette="blue">Lienzo libre</Badge>
        </HStack>
        <HStack>
          <Button size="sm" variant="outline" onClick={() => setLibrePositions({})}><Icon asChild><FaEraser /></Icon>Limpiar todo
                      </Button>
          <Button size="sm" colorPalette="blue" onClick={handleSave} loading={saving}><Icon asChild><FaSave /></Icon>Guardar
                      </Button>
        </HStack>
      </Flex>

      {/* Selector de subtipo */}
      <Flex
        mb={3}
        gap={2}
        p={2}
        bg={selectorBg}
        border="1px solid"
        borderColor={selectorBorder}
        rounded="xl"
        align="center"
        wrap="wrap"
      >
        <Text fontSize="xs" fontWeight="700" color="gray.500" mr={1}>Tipo de escenario:</Text>
        {[
          { id: 'teatro', label: '🎭 Teatro / Auditorio', desc: 'Distribución semicircular' },
          { id: 'calle',  label: '🥁 Calle / Desfile',   desc: 'Distribución en calzada' },
        ].map(opt => (
          <Box
            key={opt.id}
            px={3}
            py={1.5}
            rounded="lg"
            border="2px solid"
            borderColor={subtipoLibre === opt.id ? 'blue.400' : selectorBorder}
            bg={subtipoLibre === opt.id ? 'blue.50' : 'transparent'}
            cursor="pointer"
            onClick={() => setSubtipoLibre(opt.id)}
            transition="all 0.15s"
            _dark={{ bg: subtipoLibre === opt.id ? 'blue.900' : 'transparent' }}
          >
            <Text fontSize="xs" fontWeight="700" color={subtipoLibre === opt.id ? 'blue.600' : 'inherit'}>
              {opt.label}
            </Text>
            <Text fontSize="2xs" color="gray.400">{opt.desc}</Text>
          </Box>
        ))}
      </Flex>

      <Flex gap={4} direction={{ base: 'column', lg: 'row' }} align="flex-start">
        {/* ── Paleta de músicos / grupos ── */}
        <Box
          w={{ base: '100%', lg: '200px' }}
          minW={{ lg: '200px' }}
          maxH={{ lg: 'calc(100vh - 280px)' }}
          overflowY="auto"
          bg={sideBg}
          rounded="xl"
          shadow="md"
          p={3}
        >
          <Tabs.Root size="sm" variant='subtle' colorPalette="purple" value={tabsControlledValue(sideTab)} onValueChange={tabsOnValueChange(setSideTab)}>
            <Tabs.List mb={2}>
              <Tab value="0" fontSize="2xs" px={2}>Músicos</Tab>
              <Tab value="1" fontSize="2xs" px={2}>Grupos de músicos</Tab>
            </Tabs.List>
            <TabPanels>
              {/* ── Pestaña Músicos ── */}
              <TabPanel value="0" px={0} py={0}>
                <Text fontSize="2xs" color="gray.400" mb={2}>
                  Haz clic en un músico y luego en el lienzo para colocarlo.
                </Text>
                <HStack gap={1} mb={2}>
                  <Button size="xs" variant={sortOrder === 'familia' ? 'solid' : 'ghost'}
                    colorPalette={sortOrder === 'familia' ? 'purple' : 'gray'}
                    onClick={() => setSortOrder('familia')} flex={1} fontSize="2xs" h="22px">
                    Familia
                  </Button>
                  <Button size="xs" variant={sortOrder === 'alfabetico' ? 'solid' : 'ghost'}
                    colorPalette={sortOrder === 'alfabetico' ? 'purple' : 'gray'}
                    onClick={() => setSortOrder('alfabetico')} flex={1} fontSize="2xs" h="22px">
                    A–Z
                  </Button>
                </HStack>
                <Badge colorPalette="green" mb={2} fontSize="2xs">{availableMusicos.length} disponibles</Badge>
                <Separator mb={2} />
                {availableMusicos.length === 0 ? (
                  <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>Todos colocados</Text>
                ) : (
                  <VStack gap={1} align="stretch">
                    {availableMusicos.map(m => {
                      const familia = primeraFamiliaMusico(m) || 'OTRO';
                      const colors = FAMILIA_COLORS[familia] || FAMILIA_COLORS.OTRO;
                      const mFirstIa = m?.instrumentosAsignados?.[0] || null;
                      const mFirstNombre = mFirstIa?.instrumento?.nombre || mFirstIa?.instrumentoPersonalizado?.nombre || null;
                      const isSelected = selectedMusico?.id === m.id;
                      return (
                        <Box
                          key={m.id}
                          bg={isSelected ? 'blue.50' : colors.bg}
                          border="2px solid"
                          borderColor={isSelected ? 'blue.400' : colors.border}
                          rounded="lg" px={2} py={1.5} cursor="grab"
                          onMouseDown={(e) => {
                            if (e.button !== 0) return;
                            e.preventDefault();
                            setSelectedGrupo(null);
                            setDragIntent({ musico: m, startX: e.clientX, startY: e.clientY, isDragging: false, cursorX: e.clientX, cursorY: e.clientY });
                          }}
                          transition="all 0.15s" _hover={{ shadow: 'md' }} userSelect="none"
                        >
                          <HStack gap={2}>
                            <InstrIcon nombre={mFirstNombre} familia={familia} boxSize={5} color={isSelected ? 'blue.600' : colors.text} />
                            <VStack gap={0} align="start" flex={1} minW={0}>
                              <Text fontWeight="700" fontSize="xs" color={isSelected ? 'blue.700' : colors.text} lineClamp={1}>
                                {m.nombre} {m.apellidos || ''}
                              </Text>
                              <Text fontSize="2xs" color="gray.500" lineClamp={1}>
                                {formatMusicoInstrumentosText(m, ' · ')}
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </TabPanel>
              {/* ── Pestaña Grupos ── */}
              <TabPanel value="1" px={0} py={0}>
                <Text fontSize="2xs" color="gray.400" mb={2}>
                  Haz clic o arrastra un grupo de músicos para colocar todos sus músicos.
                </Text>
                {(grupos || []).length === 0 ? (
                  <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>No hay grupos de músicos creados</Text>
                ) : (
                  <VStack gap={1} align="stretch">
                    {(grupos || []).map(g => {
                      const avail = getGrupoAvailableMembers(g);
                      const isSelected = selectedGrupo?.id === g.id;
                      return (
                        <Box
                          key={g.id}
                          bg={avail.length === 0 ? 'gray.100' : isSelected ? 'blue.50' : 'purple.50'}
                          border="2px solid"
                          borderColor={avail.length === 0 ? 'gray.300' : isSelected ? 'blue.400' : 'purple.300'}
                          rounded="lg" px={2} py={1.5}
                          cursor={avail.length === 0 ? 'not-allowed' : 'grab'}
                          opacity={avail.length === 0 ? 0.5 : 1}
                          onMouseDown={(e) => {
                            if (e.button !== 0 || avail.length === 0) return;
                            e.preventDefault();
                            setSelectedMusico(null);
                            setDragIntent({ grupo: g, startX: e.clientX, startY: e.clientY, isDragging: false, cursorX: e.clientX, cursorY: e.clientY });
                          }}
                          transition="all 0.15s"
                          _hover={avail.length > 0 ? { shadow: 'md' } : {}}
                          userSelect="none"
                        >
                          <HStack gap={2}>
                            <Icon
                              boxSize={4}
                              color={avail.length === 0 ? 'gray.400' : isSelected ? 'blue.600' : 'purple.500'}
                              asChild><FaUsers /></Icon>
                            <VStack gap={0} align="start" flex={1} minW={0}>
                              <Text fontWeight="700" fontSize="xs" color={avail.length === 0 ? 'gray.400' : isSelected ? 'blue.700' : 'purple.700'} lineClamp={1}>
                                {g.nombre}
                              </Text>
                              <Text fontSize="2xs" color="gray.500" lineClamp={1}>
                                {avail.length}/{g.miembros?.length || 0} disponibles
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs.Root>
        </Box>

        {/* ── Lienzo ── */}
        <Box
          flex={1}
          minW={0}
          // En escritorio queremos que el lienzo aproveche altura y pueda ampliarse.
          minH={{ base: '520px', lg: 'calc(100vh - 280px)' }}
          h={{ base: '520px', lg: 'calc(100vh - 280px)' }}
          maxH={{ lg: 'calc(100vh - 180px)' }}
          resize={{ base: 'none', lg: 'vertical' }}
          overflow={{ base: 'hidden', lg: 'auto' }}
          rounded="2xl"
          shadow="md"
          bg={canvasBg}
          borderWidth="1px"
          borderColor="blackAlpha.100"
          _dark={{ borderColor: 'whiteAlpha.200' }}
        >
          <Box
            ref={canvasRef}
            position="relative"
            w="full"
            h="full"
            minH="520px"
            overflow="hidden"
            cursor={selectedMusico || selectedGrupo || dragIntent?.isDragging ? 'crosshair' : dragging ? 'grabbing' : 'default'}
            outline={dragIntent?.isDragging ? '3px dashed' : 'none'}
            outlineColor="blue.400"
            onClick={handleCanvasClick}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseUp}
            userSelect="none"
          >
          {/* Guías según subtipo */}
          {subtipoLibre === 'teatro' ? <CanvasGuiaTeatro /> : <CanvasGuiaCalle />}

          {/* Etiqueta de referencia */}
          {subtipoLibre === 'teatro' ? (
            <Text
              position="absolute" bottom={3} left="50%" transform="translateX(-50%)"
              fontSize="2xs" color="gray.400" fontWeight="600" letterSpacing="wider" pointerEvents="none"
            >
              ESCENARIO / DIRECCIÓN
            </Text>
          ) : (
            <>
              <Text
                position="absolute" top="50%" left="3%" transform="translateY(-50%) rotate(-90deg)"
                fontSize="2xs" color="gray.400" fontWeight="600" letterSpacing="wider"
                pointerEvents="none" whiteSpace="nowrap"
              >
                ACERA
              </Text>
              <Text
                position="absolute" top="50%" right="3%" transform="translateY(-50%) rotate(90deg)"
                fontSize="2xs" color="gray.400" fontWeight="600" letterSpacing="wider"
                pointerEvents="none" whiteSpace="nowrap"
              >
                ACERA
              </Text>
              <Text
                position="absolute" top={2} left="50%" transform="translateX(-50%)"
                fontSize="2xs" color="gray.400" fontWeight="600" letterSpacing="wider"
                pointerEvents="none"
              >
                ↑ DIRECCIÓN DE MARCHA
              </Text>
            </>
          )}

          {/* Músicos colocados */}
          {Object.entries(librePositions).map(([musicoId, pos]) => {
            const familia = primeraFamiliaMusico(pos.musico) || 'OTRO';
            const ia = pos.musico.instrumentosAsignados?.find(x => x.id === pos.iaId)
              || pos.musico.instrumentosAsignados?.[0];
            const displayFam = ia ? getIaFamilia(ia) : familia;
            const iaName = ia ? getIaDisplay(ia) : null;
            const colors = FAMILIA_COLORS[displayFam] || FAMILIA_COLORS.OTRO;
            const hasMulti = (pos.musico.instrumentosAsignados?.length || 0) > 1;
            return (
              <Box
                key={musicoId}
                position="absolute"
                left={`${pos.x}%`}
                top={`${pos.y}%`}
                transform="translate(-50%, -50%)"
                bg={colors.bg}
                border="2px solid"
                borderColor={dragging?.musicoId === musicoId ? 'blue.400' : colors.border}
                rounded="xl"
                shadow={dragging?.musicoId === musicoId ? 'lg' : 'md'}
                px={2}
                py={1}
                minW="70px"
                maxW="100px"
                zIndex={dragging?.musicoId === musicoId ? 10 : 1}
                onMouseDown={e => handlePlacedMouseDown(musicoId, e)}
                cursor="grab"
                transition={dragging?.musicoId === musicoId ? 'none' : 'box-shadow 0.15s'}
              >
                <Box position="absolute" top={-1} right={-1} zIndex={2}>
                  <IconButton
                    size="xs"
                    variant="solid"
                    colorPalette="red"
                    rounded="full"
                    minW="14px"
                    h="14px"
                    fontSize="7px"
                    onClick={e => { e.stopPropagation(); handleRemove(musicoId); }}
                    aria-label="Quitar"><FaTimes /></IconButton>
                </Box>
                <VStack gap={0} align="center">
                  <InstrIcon nombre={iaName} familia={displayFam} boxSize={6} color={colors.text} />
                  <Text fontSize="2xs" fontWeight="700" textAlign="center" lineClamp={1} color={colors.text}>
                    {pos.musico.nombre}{pos.musico.apellidos ? ` ${pos.musico.apellidos.trim().split(/\s+/)[0][0]}.` : ''}
                  </Text>
                  {hasMulti ? (
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="xs"
                        variant="filled"
                        value={pos.iaId || ''}
                        onChange={e => { e.stopPropagation(); handleChangeIaLibre(musicoId, e.target.value); }}
                        fontSize="2xs"
                        h="16px"
                        borderRadius="md"
                        onMouseDown={e => e.stopPropagation()}>
                        {pos.musico.instrumentosAsignados.map(x => (
                          <option key={x.id} value={x.id}>
                            {getIaDisplay(x)}{getIaVoz(x) ? ` · ${getIaVoz(x)}` : ''}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  ) : (
                    ia && (
                      <Text fontSize="2xs" color="gray.500" textAlign="center" lineClamp={1}>
                        {getIaDisplay(ia)}{getIaVoz(ia) ? ` · ${getIaVoz(ia)}` : ''}
                      </Text>
                    )
                  )}
                </VStack>
              </Box>
            );
          })}

          {/* Indicador "haz clic para colocar" */}
          {(selectedMusico || selectedGrupo) && (
            <Box
              position="absolute"
              bottom={8}
              left="50%"
              transform="translateX(-50%)"
              bg={selectedGrupo ? 'purple.600' : 'blue.600'}
              color="white"
              px={4}
              py={1.5}
              rounded="full"
              fontSize="xs"
              fontWeight="700"
              shadow="lg"
              pointerEvents="none"
            >
              {selectedGrupo
                ? `Haz clic para colocar grupo "${selectedGrupo.nombre}"`
                : `Haz clic para colocar a ${selectedMusico.nombre}`}
            </Box>
          )}
          </Box>
        </Box>
      </Flex>

      {/* Ghost: sigue al cursor mientras se arrastra desde la paleta */}
      {dragIntent?.isDragging && (() => {
        if (dragIntent.grupo) {
          const avail = getGrupoAvailableMembers(dragIntent.grupo);
          return (
            <Box
              position="fixed"
              left={`${dragIntent.cursorX + 12}px`}
              top={`${dragIntent.cursorY - 24}px`}
              zIndex={9999}
              pointerEvents="none"
              bg="purple.50"
              border="2px solid"
              borderColor="purple.400"
              rounded="xl"
              px={3}
              py={1.5}
              shadow="xl"
              opacity={0.92}
            >
              <HStack gap={2}>
                <Icon boxSize={5} color="purple.500" asChild><FaUsers /></Icon>
                <VStack gap={0} align="start">
                  <Text fontSize="xs" fontWeight="700" color="purple.700">
                    {dragIntent.grupo.nombre}
                  </Text>
                  <Text fontSize="2xs" color="gray.500">{avail.length} músicos · Suelta en el lienzo</Text>
                </VStack>
              </HStack>
            </Box>
          );
        }
        const gFamilia = primeraFamiliaMusico(dragIntent.musico) || 'OTRO';
        const gColors = FAMILIA_COLORS[gFamilia] || FAMILIA_COLORS.OTRO;
        const gFirstIa = dragIntent.musico?.instrumentosAsignados?.[0] || null;
        const gNombre = gFirstIa?.instrumento?.nombre || gFirstIa?.instrumentoPersonalizado?.nombre || null;
        return (
          <Box
            position="fixed"
            left={`${dragIntent.cursorX + 12}px`}
            top={`${dragIntent.cursorY - 24}px`}
            zIndex={9999}
            pointerEvents="none"
            bg={gColors.bg}
            border="2px solid"
            borderColor={gColors.border}
            rounded="xl"
            px={3}
            py={1.5}
            shadow="xl"
            opacity={0.92}
          >
            <HStack gap={2}>
              <InstrIcon nombre={gNombre} familia={gFamilia} boxSize={5} color={gColors.text} />
              <VStack gap={0} align="start">
                <Text fontSize="xs" fontWeight="700" color={gColors.text}>
                  {dragIntent.musico.nombre} {dragIntent.musico.apellidos || ''}
                </Text>
                <Text fontSize="2xs" color="gray.500">Suelta en el lienzo</Text>
              </VStack>
            </HStack>
          </Box>
        );
      })()}
    </Box>
  );
}

// ── FormacionEditor principal ─────────────────────────────────────────────────
/**
 * Props:
 *  - plantilla: objeto formación (id, nombre, filas, columnas…)
 *  - toast: función de toast (de Chakra)
 *  - embedded: bool – oculta el botón "volver", para uso inline en Formaciones.jsx
 *  - onBack: callback cuando no es embedded
 *  - onSaved: callback tras guardar posiciones
 */
export default function FormacionEditor({ plantilla: initialPlantilla, onBack, toast, embedded = false, onSaved }) {
  const [plantilla, setPlantilla] = useState(null);
  const [posiciones, setPosiciones] = useState([]);
  // asignaciones: { "fila-col": { musico, iaId } }
  const [asignaciones, setAsignaciones] = useState({});
  const [instrumentos, setInstrumentos] = useState([]);
  const [instrumentosPersonalizados, setInstrumentosPersonalizados] = useState([]);
  const [musicos, setMusicos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('musicians');
  const [sideTab, setSideTab] = useState(0);
  const [activeDrag, setActiveDrag] = useState(null);
  const [uncoveredCells, setUncoveredCells] = useState([]);
  const [sortOrder, setSortOrder] = useState('familia');
  // Dimensiones locales (pueden diferir de plantilla mientras no se guarda)
  const [localFilas, setLocalFilas] = useState(8);
  const [localColumnas, setLocalColumnas] = useState(6);
  // Fila director
  const [showDirector, setShowDirector] = useState(false);
  // Fila extraordinaria
  const [showFilaExtra, setShowFilaExtra] = useState(false);
  const [filaExtraPos, setFilaExtraPos] = useState('before'); // 'before' | 'after'
  const [filaExtraCeldas, setFilaExtraCeldas] = useState(6);
  const [filaExtraLabel, setFilaExtraLabel] = useState('Fila extraordinaria');

  const { open: isSuggestOpen, onOpen: onSuggestOpen, onClose: onSuggestClose } = useDisclosure();
  const [suggestions, setSuggestions] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const gridBg = useColorModeValue('gray.100', 'gray.700');
  const sideBg = useColorModeValue('white', 'gray.800');
  const panelBorder = useColorModeValue('gray.200', 'gray.600');
  const filaExtraInputBg = useColorModeValue('white', 'gray.700');

  // ── Carga de datos ────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, iRes, ipRes, mRes, gRes] = await Promise.all([
        formacionesAPI.getPlantilla(initialPlantilla.id),
        instrumentosAPI.getAll(),
        instrumentosAPI.getPersonalizados(),
        musicosAPI.getAll(),
        musicosAPI.getGrupos(),
      ]);
      const p = pRes.data;
      setPlantilla(p);
      setPosiciones(p.posiciones || []);
      setInstrumentos(iRes.data);
      setInstrumentosPersonalizados(ipRes.data);
      setMusicos(mRes.data.filter(m => m.activo));
      setGrupos(gRes.data || []);
      const pos = p.posiciones || [];
      const allMusicos = mRes.data.filter(m => m.activo);
      // Restaurar asignaciones de músicos desde posiciones guardadas
      const restoredAsignaciones = {};
      pos.forEach(posc => {
        if (posc.musicoId) {
          const key = `${posc.fila}-${posc.columna}`;
          // Usar el músico del listado completo para tener todas las relaciones
          const musico = allMusicos.find(m => m.id === posc.musicoId) || posc.musico;
          if (musico) {
            const ia = musico.instrumentosAsignados?.find(x =>
              x.instrumento?.id === posc.instrumentoId ||
              x.instrumentoPersonalizado?.id === posc.instrumentoPersonalizadoId
            ) || musico.instrumentosAsignados?.[0];
            restoredAsignaciones[key] = { musico, iaId: ia?.id || null };
          }
        }
      });
      setAsignaciones(restoredAsignaciones);

      // Las posiciones con músico viven en `asignaciones`; aquí solo reservas de instrumento.
      setPosiciones(pos.filter((p) => !p.musicoId));
      // Si hay posiciones en filas especiales, activar el flag aunque la BD diga false
      // (compatibilidad con guardados anteriores al fix).
      const hasDirectorPos   = pos.some(x => x.fila === DIRECTOR_ROW);
      const hasExtraBeforePos = pos.some(x => x.fila === EXTRA_BEFORE);
      const hasExtraAfterPos  = pos.some(x => x.fila === EXTRA_AFTER);
      const hasExtraPos = hasExtraBeforePos || hasExtraAfterPos;
      setLocalFilas(p.filas || 8);
      setLocalColumnas(p.columnas || 6);
      setShowDirector((p.tieneDirector ?? false) || hasDirectorPos);
      setShowFilaExtra((p.tieneFilaExtra ?? false) || hasExtraPos);
      setFilaExtraPos(hasExtraBeforePos ? 'before' : hasExtraAfterPos ? 'after' : (p.filaExtraPos || 'before'));
      setFilaExtraCeldas(p.filaExtraCeldas || p.columnas || 6);
      setFilaExtraLabel(p.filaExtraEtiqueta || 'Fila extraordinaria');
    } catch (err) {
      console.error(err);
      toast({ title: 'Error cargando datos', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [initialPlantilla.id, toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Mapas derivados ───────────────────────────────────────────────────────
  const posMap = useMemo(() => {
    const map = {};
    posiciones.forEach((p) => {
      if (!p.musicoId) map[`${p.fila}-${p.columna}`] = p;
    });
    return map;
  }, [posiciones]);

  const assignedMusicoIds = useMemo(() => {
    const activeSpecials = new Set();
    if (showDirector)  activeSpecials.add(DIRECTOR_ROW);
    if (showFilaExtra) activeSpecials.add(filaExtraPos === 'before' ? EXTRA_BEFORE : EXTRA_AFTER);
    return new Set(
      Object.entries(asignaciones)
        .filter(([key, val]) => {
          if (!val?.musico) return false;
          const lastDash = key.lastIndexOf('-');
          const fila = parseInt(key.slice(0, lastDash), 10);
          const col  = parseInt(key.slice(lastDash + 1), 10);
          // Fila especial: solo contar si está activa
          if (fila < 0) return activeSpecials.has(fila);
          // Fila normal: solo contar si está dentro de los límites del grid visible
          if (fila >= localFilas || col >= localColumnas) return false;
          return true;
        })
        .map(([, val]) => val.musico.id)
    );
  }, [asignaciones, showDirector, showFilaExtra, filaExtraPos, localFilas, localColumnas]);

  const availableMusicos = useMemo(() =>
    sortMusicosList(musicos.filter(m => !assignedMusicoIds.has(m.id)), sortOrder),
  [musicos, assignedMusicoIds, sortOrder]);

  const groupedInstruments = useMemo(() => {
    const groups = {};
    Object.keys(FAMILIA_LABELS).forEach(f => { groups[f] = []; });
    instrumentos.forEach(i => groups[i.familia]?.push({ ...i, type: 'catalog' }));
    instrumentosPersonalizados.forEach(i => groups[i.familia]?.push({ ...i, type: 'custom' }));
    return groups;
  }, [instrumentos, instrumentosPersonalizados]);

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = e => {
    const data = e.active.data.current;
    // Normalizar: si viene de celda, exponer musico directamente para el overlay
    setActiveDrag(data);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    const overData = over.data.current;
    if (!overData || overData.row === undefined) return;

    const { row, col } = overData;
    const cellKey = `${row}-${col}`;
    const activeData = active.data.current;

    if (activeData.fromCell) {
      // Arrastre desde una celda del grid hacia otra celda
      const srcRow = activeData.row;
      const srcCol = activeData.col;
      if (srcRow === row && srcCol === col) return; // misma celda, nada que hacer
      const srcKey = `${srcRow}-${srcCol}`;
      setAsignaciones(prev => {
        const updated = { ...prev };
        const srcVal = updated[srcKey];
        const dstVal = updated[cellKey];
        if (dstVal) {
          // Intercambio
          updated[srcKey] = dstVal;
          updated[cellKey] = srcVal;
        } else {
          // Mover
          delete updated[srcKey];
          updated[cellKey] = srcVal;
        }
        return updated;
      });
      setPosiciones((prev) =>
        withoutMusicianPositions(prev, [
          { row: srcRow, col: srcCol },
          { row, col },
        ]),
      );
    } else if (activeData.instrument) {
      // Placing an instrument slot
      const inst = activeData.instrument;
      const type = activeData.type;
      const newPos = {
        fila: row,
        columna: col,
        instrumentoId: type === 'catalog' ? inst.id : null,
        instrumentoPersonalizadoId: type === 'custom' ? inst.id : null,
        label: inst.nombre,
        instrumento: type === 'catalog' ? inst : null,
        instrumentoPersonalizado: type === 'custom' ? inst : null,
      };
      setPosiciones(prev => [
        ...prev.filter(p => !(p.fila === row && p.columna === col)),
        newPos,
      ]);
    } else if (activeData.grupo) {
      const grupo = activeData.grupo;
      const members = (grupo.miembros || [])
        .map(gm => musicos.find(m => m.id === (gm.musico?.id || gm.musicoId)))
        .filter(m => m && !assignedMusicoIds.has(m.id));
      if (members.length === 0) return;
      setAsignaciones(prev => {
        const updated = { ...prev };
        let r = row;
        let c = col;
        for (const musico of members) {
          while (r < localFilas) {
            if (c >= localColumnas) { r++; c = 0; continue; }
            const k = `${r}-${c}`;
            if (!updated[k]) break;
            c++;
          }
          if (r >= localFilas) break;
          updated[`${r}-${c}`] = { musico, iaId: getFirstIaId(musico) };
          c++;
        }
        return updated;
      });
    } else if (activeData.musico) {
      const musico = activeData.musico;
      const iaId = getFirstIaId(musico);
      const prevKey = Object.entries(asignaciones).find(([, v]) => v.musico?.id === musico.id)?.[0];
      const cellsToClean = [{ row, col }];
      if (prevKey) {
        const lastDash = prevKey.lastIndexOf('-');
        cellsToClean.push({
          row: parseInt(prevKey.slice(0, lastDash), 10),
          col: parseInt(prevKey.slice(lastDash + 1), 10),
        });
      }
      setAsignaciones(prev => {
        const updated = { ...prev };
        if (prevKey) delete updated[prevKey];
        updated[cellKey] = { musico, iaId };
        return updated;
      });
      setPosiciones((prev) => withoutMusicianPositions(prev, cellsToClean));
    }
  };

  // ── Acciones ──────────────────────────────────────────────────────────────
  const handleClearCell = (row, col) => {
    const key = `${row}-${col}`;
    setPosiciones(prev => prev.filter(p => !(p.fila === row && p.columna === col)));
    setAsignaciones(prev => { const u = { ...prev }; delete u[key]; return u; });
  };

  const handleChangeIa = (row, col, iaId) => {
    const key = `${row}-${col}`;
    setAsignaciones(prev => ({
      ...prev,
      [key]: { ...prev[key], iaId },
    }));
  };

  const handleClearAll = () => {
    setPosiciones([]);
    setAsignaciones({});
    setUncoveredCells([]);
  };

  const handleSavePosiciones = async () => {
    try {
      setSaving(true);
      const allowedSpecials = new Set();
      if (showDirector) allowedSpecials.add(DIRECTOR_ROW);
      if (showFilaExtra) allowedSpecials.add(filaExtraPos === 'before' ? EXTRA_BEFORE : EXTRA_AFTER);

      // Construir payload: una entrada por cada asignación de músico
      const posData = Object.entries(asignaciones)
        .filter(([, val]) => val?.musico)
        .map(([key, val]) => {
          // Las filas negativas generan claves como "-1-0", así que buscamos
          // el último guión para separar fila y columna correctamente.
          const lastDash = key.lastIndexOf('-');
          const fila = parseInt(key.slice(0, lastDash), 10);
          const col  = parseInt(key.slice(lastDash + 1), 10);
          // Descartar posiciones fuera de los límites del grid o de filas especiales inactivas
          if (fila < 0 && !allowedSpecials.has(fila)) return null;
          if (fila >= 0 && (fila >= localFilas || col >= localColumnas)) return null;
          const ia = val.musico.instrumentosAsignados?.find(x => x.id === val.iaId)
                  || val.musico.instrumentosAsignados?.[0]
                  || null;
          return {
            fila,
            columna: col,
            musicoId: val.musico.id,
            instrumentoId: ia?.instrumento?.id || null,
            instrumentoPersonalizadoId: ia?.instrumentoPersonalizado?.id || null,
            label: val.musico.nombre,
          };
        })
        .filter(Boolean);

      // Siempre persistir la configuración del grid (dimensiones + flags)
      await formacionesAPI.updatePlantilla(plantilla.id, {
        filas: localFilas,
        columnas: localColumnas,
        tieneDirector: showDirector,
        tieneFilaExtra: showFilaExtra,
        filaExtraPos,
        filaExtraCeldas,
        filaExtraEtiqueta: filaExtraLabel || null,
      });
      await formacionesAPI.savePosiciones(plantilla.id, posData);
      toast({ title: 'Formación guardada', status: 'success' });
      onSaved?.();
    } catch {
      toast({ title: 'Error al guardar', status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAutoSuggest = async () => {
    try {
      const res = await formacionesAPI.autoSuggest(plantilla.id);
      setSuggestions(res.data);
      onSuggestOpen();
    } catch {
      toast({ title: 'Error', status: 'error' });
    }
  };

  const handleApplySuggestions = () => {
    if (!suggestions) return;
    const newAsignaciones = {};
    suggestions.suggestions.forEach(s => {
      const musico = musicos.find(m => m.id === s.musicoId);
      if (musico) {
        newAsignaciones[`${s.fila}-${s.columna}`] = { musico, iaId: getFirstIaId(musico) };
      }
    });
    setAsignaciones(newAsignaciones);
    setUncoveredCells(suggestions.uncovered.map(u => `${u.fila}-${u.columna}`));
    onSuggestClose();
    toast({
      title: 'Auto-sugerencia aplicada',
      description: `${suggestions.suggestions.length} asignados, ${suggestions.uncovered.length} sin cubrir`,
      status: suggestions.uncovered.length > 0 ? 'warning' : 'success',
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading || !plantilla) {
    return <Flex justify="center" align="center" h="300px"><Spinner size="xl" color="purple.500" /></Flex>;
  }

  // ── Modo LIBRE: editor de lienzo libre ──
  if (plantilla.modo === 'LIBRE') {
    return (
      <LibreCanvasEditor
        plantilla={plantilla}
        musicos={musicos}
        posiciones={posiciones}
        toast={toast}
        onSaved={onSaved}
        grupos={grupos}
      />
    );
  }

  // Mínimo de filas/columnas para cubrir todos los músicos
  const minFilas    = Math.max(1, Math.ceil(musicos.length / Math.max(1, localColumnas)));
  const minColumnas = Math.max(1, Math.ceil(musicos.length / Math.max(1, localFilas)));

  return (
    <Box px={{ base: 2, md: 4 }} py={{ base: 3, md: 4 }}>
      {/* ── Cabecera ── */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={2} mb={4}>
        <HStack>
          <Heading size="md">{plantilla.nombre}</Heading>
          <Badge colorPalette="purple">{localFilas} × {localColumnas}</Badge>
          {(localFilas !== plantilla.filas || localColumnas !== plantilla.columnas) && (
            <Badge colorPalette="orange" fontSize="2xs">sin guardar</Badge>
          )}
        </HStack>
        <HStack>
          <Tooltip content="Limpiar todo">
            <Button size="sm" variant="outline" onClick={handleClearAll}><FaEraser />Limpiar
                          </Button>
          </Tooltip>
          <Button
            size="sm"
            colorPalette="purple"
            onClick={handleSavePosiciones}
            loading={saving}><FaSave />Guardar
                      </Button>
        </HStack>
      </Flex>

      {/* ── Panel de configuración del grid ── */}
      <Box
        bg={sideBg}
        border="1px solid"
        borderColor={panelBorder}
        rounded="xl"
        p={4}
        mb={4}
        shadow="sm"
      >
        <Flex wrap="wrap" gap={6} align="flex-start">
          {/* Dimensiones */}
          <VStack gap={2} align="start">
            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">Dimensiones</Text>
            <HStack gap={3} align="flex-end">
              <Field.Root>
                <Field.Label fontSize="2xs" mb={0.5} color="gray.500">Filas</Field.Label>
                <NumberInput.Root
                  size="sm"
                  min={minFilas}
                  max={50}
                  value={String(localFilas)}
                  onValueChange={(d) => { const v = Number(d.value); if (isNaN(v)) return; setLocalFilas(Math.max(minFilas, v)); }}
                  w="80px"
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>
              <Text mb={1} color="gray.400" fontWeight="700">×</Text>
              <Field.Root>
                <Field.Label fontSize="2xs" mb={0.5} color="gray.500">Columnas</Field.Label>
                <NumberInput.Root
                  size="sm"
                  min={minColumnas}
                  max={30}
                  value={String(localColumnas)}
                  onValueChange={(d) => { const v = Number(d.value); if (isNaN(v)) return; setLocalColumnas(Math.max(minColumnas, v)); }}
                  w="80px"
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>
            </HStack>
            <Text fontSize="2xs" color="gray.400">
              {musicos.length} músicos · mín. {minFilas}×{minColumnas}
            </Text>
          </VStack>

          <Separator orientation="vertical" alignSelf="stretch" />

          {/* Director */}
          <VStack gap={2} align="start">
            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">Director</Text>
            <HStack>
              <Switch
                checked={showDirector}
                onValueChange={e => setShowDirector(e.target.checked)}
                colorPalette="purple"
                size="sm"
              />
              <Text fontSize="xs" color={showDirector ? 'purple.500' : 'gray.400'}>
                {showDirector ? 'Activado' : 'Desactivado'}
              </Text>
            </HStack>
            {showDirector && (
              <Text fontSize="2xs" color="gray.400">Celda centrada delante del grid</Text>
            )}
          </VStack>

          <Separator orientation="vertical" alignSelf="stretch" />

          {/* Fila extraordinaria */}
          <VStack gap={2} align="start">
            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">Fila extraordinaria</Text>
            <HStack>
              <Switch
                checked={showFilaExtra}
                onValueChange={e => setShowFilaExtra(e.target.checked)}
                colorPalette="orange"
                size="sm"
              />
              <Text fontSize="xs" color={showFilaExtra ? 'orange.500' : 'gray.400'}>
                {showFilaExtra ? 'Activada' : 'Desactivada'}
              </Text>
            </HStack>
            {showFilaExtra && (
              <VStack gap={2} align="start">
                <HStack gap={2}>
                  <Text fontSize="2xs" color="gray.500" whiteSpace="nowrap">Etiqueta:</Text>
                  <Box
                    fontSize="xs"
                    borderWidth="1px"
                    borderColor={panelBorder}
                    rounded="md"
                    px={2}
                    py={0.5}
                    bg={filaExtraInputBg}
                    color="inherit"
                    w="140px"
                    asChild><input
                      value={filaExtraLabel}
                      onChange={e => setFilaExtraLabel(e.target.value)} /></Box>
                </HStack>
                <HStack gap={2}>
                  <Text fontSize="2xs" color="gray.500" whiteSpace="nowrap">Posición:</Text>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      size="xs"
                      value={filaExtraPos}
                      onChange={e => setFilaExtraPos(e.target.value)}
                      w="160px">
                      <option value="before">Delante del director</option>
                      <option value="after">Detrás del director</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </HStack>
                <HStack gap={2} align="flex-end">
                  <Field.Root>
                    <Field.Label fontSize="2xs" mb={0.5} color="gray.500">Nº celdas</Field.Label>
                    <NumberInput.Root
                      size="xs"
                      min={1}
                      max={20}
                      value={String(filaExtraCeldas)}
                      onValueChange={(d) => { const v = Number(d.value); if (isNaN(v)) return; setFilaExtraCeldas(Math.max(1, v)); }}
                      w="70px"
                    >
                      <NumberInput.Input />
                      <NumberInput.Control>
                        <NumberInput.IncrementTrigger />
                        <NumberInput.DecrementTrigger />
                      </NumberInput.Control>
                    </NumberInput.Root>
                  </Field.Root>
                </HStack>
              </VStack>
            )}
          </VStack>
        </Flex>
      </Box>

      {/* ── DnD: paleta + grid ── */}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Flex gap={4} direction={{ base: 'column', lg: 'row' }} align="flex-start">

          {/* Paleta lateral */}
          <Box
            w={{ base: '100%', lg: '220px' }}
            minW={{ lg: '220px' }}
            maxH={{ lg: 'calc(100vh - 260px)' }}
            overflowY="auto"
            bg={sideBg}
            rounded="xl"
            shadow="md"
            p={3}
          >
            {mode === 'instruments' ? (
              <VStack align="stretch" gap={3}>
                <Text fontSize="xs" fontWeight="700" color="gray.500">Instrumentos</Text>
                <Text fontSize="2xs" color="gray.400">Arrastra al grid para definir posiciones</Text>
                {Object.entries(groupedInstruments).map(([familia, items]) =>
                  items.length > 0 && (
                    <Box key={familia}>
                      <HStack mb={1} gap={1}>
                        <Text>{FAMILIA_EMOJI[familia]}</Text>
                        <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase">
                          {FAMILIA_LABELS[familia]}
                        </Text>
                      </HStack>
                      <VStack align="stretch" gap={1}>
                        {items.map(i => (
                          <DraggableInstrument key={`${i.type}-${i.id}`} instrument={i} type={i.type} />
                        ))}
                      </VStack>
                    </Box>
                  )
                )}
              </VStack>
            ) : (
              <Tabs.Root size="sm" variant='subtle' colorPalette="purple" value={tabsControlledValue(sideTab)} onValueChange={tabsOnValueChange(setSideTab)}>
                <Tabs.List mb={2}>
                  <Tab value="0" fontSize="2xs" px={2}>Músicos</Tab>
                  <Tab value="1" fontSize="2xs" px={2}>Grupos de músicos</Tab>
                </Tabs.List>
                <TabPanels>
                  {/* ── Pestaña Músicos ── */}
                  <TabPanel value="0" px={0} py={0}>
                    <VStack align="stretch" gap={2}>
                      <Text fontSize="2xs" color="gray.400">Arrastra al grid para colocar</Text>
                      <HStack gap={1}>
                        <Button size="xs" variant={sortOrder === 'familia' ? 'solid' : 'ghost'}
                          colorPalette={sortOrder === 'familia' ? 'purple' : 'gray'}
                          onClick={() => setSortOrder('familia')} flex={1} fontSize="2xs" h="22px">
                          Familia
                        </Button>
                        <Button size="xs" variant={sortOrder === 'alfabetico' ? 'solid' : 'ghost'}
                          colorPalette={sortOrder === 'alfabetico' ? 'purple' : 'gray'}
                          onClick={() => setSortOrder('alfabetico')} flex={1} fontSize="2xs" h="22px">
                          A–Z
                        </Button>
                      </HStack>
                      <Badge colorPalette="green" alignSelf="start" fontSize="2xs">
                        {availableMusicos.length} sin colocar
                      </Badge>
                      <Separator />
                      {availableMusicos.length === 0 ? (
                        <Text fontSize="xs" color="gray.500" textAlign="center" py={4}>
                          Todos los músicos están colocados
                        </Text>
                      ) : (
                        availableMusicos.map(m => (
                          <DraggableMusician key={m.id} musico={m} />
                        ))
                      )}
                    </VStack>
                  </TabPanel>
                  {/* ── Pestaña Grupos ── */}
                  <TabPanel value="1" px={0} py={0}>
                    <VStack align="stretch" gap={2}>
                      <Text fontSize="2xs" color="gray.400">Arrastra un grupo de músicos al grid para colocar todos sus músicos</Text>
                      {grupos.length === 0 ? (
                        <Text fontSize="xs" color="gray.500" textAlign="center" py={4}>
                          No hay grupos de músicos creados
                        </Text>
                      ) : (
                        grupos.map(g => {
                          const avail = (g.miembros || [])
                            .map(gm => musicos.find(m => m.id === (gm.musico?.id || gm.musicoId)))
                            .filter(m => m && !assignedMusicoIds.has(m.id));
                          return (
                            <DraggableGroup key={g.id} grupo={g} availableCount={avail.length} />
                          );
                        })
                      )}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs.Root>
            )}
          </Box>

          {/* Grid + filas especiales */}
          <Box flex={1} overflowX="auto">
            {/* ── Indicador de cabeza ── */}
            <Flex align="center" justify="center" mb={2}>
              <Badge colorPalette="gray" fontSize="2xs" variant="outline" px={2}>↑ Cabeza / Frente</Badge>
            </Flex>

            {/* Fila extraordinaria DELANTE del director */}
            {showFilaExtra && filaExtraPos === 'before' && (() => {
              const filaIdx = EXTRA_BEFORE;
              return (
                <Box mb={1.5}>
                  <Flex align="center" gap={2} mb={1}>
                    <Badge colorPalette="orange" fontSize="2xs">{filaExtraLabel}</Badge>
                    <Text fontSize="2xs" color="gray.400">{filaExtraCeldas} celdas</Text>
                  </Flex>
                  <Box
                    display="grid"
                    gridTemplateColumns={`repeat(${filaExtraCeldas}, minmax(75px, 1fr))`}
                    gap={1.5}
                    p={2}
                    bg="orange.50"
                    _dark={{ bg: 'orange.900' }}
                    rounded="lg"
                    border="1.5px dashed"
                    borderColor="orange.300"
                    minW={`${filaExtraCeldas * 85}px`}
                  >
                    {Array.from({ length: filaExtraCeldas }, (_, c) => {
                      const key = `${filaIdx}-${c}`;
                      return (
                        <GridCell
                          key={key}
                          row={filaIdx}
                          col={c}
                          position={posMap[key]}
                          asignacion={asignaciones[key] || null}
                          onClear={handleClearCell}
                          isHighlight={false}
                          onChangeIa={handleChangeIa}
                          specialLabel={posMap[key] ? undefined : `${filaExtraLabel} ${c + 1}`}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}

            {/* Fila director */}
            {showDirector && (() => {
              const filaIdx = DIRECTOR_ROW;
              const centerCol = 0;
              const key = `${filaIdx}-${centerCol}`;
              return (
                <Box mb={1.5}>
                  <Flex align="center" gap={2} mb={1}>
                    <Badge colorPalette="purple" fontSize="2xs">Director</Badge>
                    <Text fontSize="2xs" color="gray.400">celda centrada</Text>
                  </Flex>
                  <Flex justify="center">
                    <Box w="90px">
                      <GridCell
                        row={filaIdx}
                        col={centerCol}
                        position={posMap[key]}
                        asignacion={asignaciones[key] || null}
                        onClear={handleClearCell}
                        isHighlight={false}
                        onChangeIa={handleChangeIa}
                        specialLabel={posMap[key] ? undefined : 'Director'}
                      />
                    </Box>
                  </Flex>
                </Box>
              );
            })()}

            {/* Fila extraordinaria DETRÁS del director */}
            {showFilaExtra && filaExtraPos === 'after' && (() => {
              const filaIdx = EXTRA_AFTER;
              return (
                <Box mb={1.5}>
                  <Flex align="center" gap={2} mb={1}>
                    <Badge colorPalette="orange" fontSize="2xs">{filaExtraLabel}</Badge>
                    <Text fontSize="2xs" color="gray.400">{filaExtraCeldas} celdas · detrás del director</Text>
                  </Flex>
                  <Box
                    display="grid"
                    gridTemplateColumns={`repeat(${filaExtraCeldas}, minmax(75px, 1fr))`}
                    gap={1.5}
                    p={2}
                    bg="orange.50"
                    _dark={{ bg: 'orange.900' }}
                    rounded="lg"
                    border="1.5px dashed"
                    borderColor="orange.300"
                    minW={`${filaExtraCeldas * 85}px`}
                  >
                    {Array.from({ length: filaExtraCeldas }, (_, c) => {
                      const key = `${filaIdx}-${c}`;
                      return (
                        <GridCell
                          key={key}
                          row={filaIdx}
                          col={c}
                          position={posMap[key]}
                          asignacion={asignaciones[key] || null}
                          onClear={handleClearCell}
                          isHighlight={false}
                          onChangeIa={handleChangeIa}
                          specialLabel={posMap[key] ? undefined : `${filaExtraLabel} ${c + 1}`}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}

            {/* Separador visual */}
            {(showDirector || showFilaExtra) && (
              <Separator mb={2} borderColor="gray.300" />
            )}

            {/* Grid principal */}
            <Box
              display="grid"
              gridTemplateColumns={`repeat(${localColumnas}, minmax(75px, 1fr))`}
              gap={1.5}
              p={3}
              bg={gridBg}
              rounded="xl"
              shadow="md"
              minW={`${localColumnas * 85}px`}
            >
              {Array.from({ length: localFilas }, (_, r) =>
                Array.from({ length: localColumnas }, (_, c) => {
                  const key = `${r}-${c}`;
                  return (
                    <GridCell
                      key={key}
                      row={r}
                      col={c}
                      position={posMap[key]}
                      asignacion={asignaciones[key] || null}
                      onClear={handleClearCell}
                      isHighlight={uncoveredCells.includes(key)}
                      onChangeIa={handleChangeIa}
                    />
                  );
                })
              )}
            </Box>

          </Box>
        </Flex>

        {/* Overlay de arrastre */}
        <DragOverlay>
          {activeDrag?.instrument && (
            <Box
              bg={FAMILIA_COLORS[activeDrag.instrument.familia]?.bg || '#f5f5f5'}
              border="2px solid"
              borderColor={FAMILIA_COLORS[activeDrag.instrument.familia]?.border || '#bbb'}
              rounded="lg"
              px={2}
              py={1.5}
              fontSize="xs"
              fontWeight="600"
              shadow="lg"
            >
              <HStack gap={1}>
                <InstrIcon nombre={activeDrag.instrument.nombre} familia={activeDrag.instrument.familia} boxSize={4} color={(FAMILIA_COLORS[activeDrag.instrument.familia] || FAMILIA_COLORS.OTRO).text} />
                <Text>{activeDrag.instrument.nombre}</Text>
              </HStack>
            </Box>
          )}
          {activeDrag?.grupo && (() => {
            const g = activeDrag.grupo;
            const avail = (g.miembros || [])
              .map(gm => musicos.find(m => m.id === (gm.musico?.id || gm.musicoId)))
              .filter(m => m && !assignedMusicoIds.has(m.id));
            return (
              <Box
                bg="purple.50"
                border="2px solid"
                borderColor="purple.400"
                rounded="xl"
                px={3}
                py={2}
                shadow="xl"
                opacity={0.9}
              >
                <HStack gap={2}>
                  <Icon boxSize={5} color="purple.500" asChild><FaUsers /></Icon>
                  <VStack gap={0} align="start">
                    <Text fontWeight="700" fontSize="xs" color="purple.700">{g.nombre}</Text>
                    <Text fontSize="2xs" color="gray.500">{avail.length} músicos</Text>
                  </VStack>
                </HStack>
              </Box>
            );
          })()}
          {(activeDrag?.musico || activeDrag?.fromCell) && (() => {
            const m = activeDrag.musico;
            if (!m) return null;
            const familia = primeraFamiliaMusico(m) || 'OTRO';
            const colors = FAMILIA_COLORS[familia];
            const dragFirstIa = m?.instrumentosAsignados?.[0] || null;
            const dragFirstNombre = dragFirstIa?.instrumento?.nombre || dragFirstIa?.instrumentoPersonalizado?.nombre || null;
            return (
              <Box
                bg={colors.bg}
                border="2px solid"
                borderColor={colors.border}
                rounded="xl"
                px={3}
                py={2}
                shadow="xl"
                opacity={0.9}
              >
                <HStack gap={2}>
                  <InstrIcon nombre={dragFirstNombre} familia={familia} boxSize={6} color={(FAMILIA_COLORS[familia] || FAMILIA_COLORS.OTRO).text} />
                  <Text fontWeight="700" fontSize="xs" color={colors.text}>
                    {m.nombre}
                  </Text>
                </HStack>
              </Box>
            );
          })()}
        </DragOverlay>
      </DndContext>

      {/* Modal auto-sugerencia */}
      <Dialog.Root open={isSuggestOpen} size='md' onOpenChange={e => {
        if (!e.open) {
          onSuggestClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Resultado de auto-completar</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {suggestions && (
                  <VStack gap={3} align="stretch">
                    <HStack>
                      <Badge colorPalette="green" fontSize="sm">{suggestions.suggestions.length} asignados</Badge>
                      {suggestions.uncovered.length > 0 && (
                        <Badge colorPalette="red" fontSize="sm">{suggestions.uncovered.length} sin cubrir</Badge>
                      )}
                    </HStack>
                    {suggestions.uncovered.length > 0 && (
                      <Box bg="red.50" p={3} rounded="lg">
                        <HStack mb={2}>
                          <Icon color="red.500" asChild><FaExclamationTriangle /></Icon>
                          <Text fontWeight="600" fontSize="sm" color="red.700">Posiciones sin cubrir:</Text>
                        </HStack>
                        {suggestions.uncovered.map((u, i) => (
                          <Text key={i} fontSize="xs" color="red.600">
                            Fila {u.fila + 1}, Col {u.columna + 1}: {u.instrumentoNombre}
                          </Text>
                        ))}
                      </Box>
                    )}
                    <Text fontSize="sm" color="gray.600">
                      Se reemplazarán las asignaciones actuales. ¿Aplicar?
                    </Text>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onSuggestClose}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleApplySuggestions}><FaMagic />Aplicar sugerencia
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </Box>
  );
}
